import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  IntroSection,
  RegistrationSection,
  ScopeSection,
  UsageExamplesSection,
  LicenseSection,
  CurrentUsersSection,
  GovernmentSection,
  CasesSection,
} from './components/ContentSections';
import FAQSection from './components/FAQSection';
import ClosingSection from './components/ClosingSection';
import TableOfContents from './components/TableOfContents';

export default function TrademarkGuidelinesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState('');
  const [lightboxAlt, setLightboxAlt] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = '「パタカラ®」商標利用ガイドライン｜株式会社パックスシステムズ';

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', '登録商標「パタカラ®」の実施、名称、公式ロゴを安心してご利用いただくための商標利用ガイドラインです。介護・医療施設向け公式ライセンス、主な利用例、自治体・官公庁等の非営利利用についてご案内します。');

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '「パタカラ®」商標利用ガイドライン｜株式会社パックスシステムズ',
        description: '登録商標「パタカラ®」の実施、名称、公式ロゴを安心してご利用いただくための商標利用ガイドラインです。介護・医療施設向け公式ライセンス、主な利用例、自治体・官公庁等の非営利利用についてご案内します。',
        url: `${siteUrl}/trademark-guidelines`,
        inLanguage: 'ja',
        isPartOf: {
          '@type': 'WebSite',
          name: 'パタカラ®公式施設ライセンス',
          url: `${siteUrl}/`,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '名称やロゴを掲載しなければ、施設で自由に実施できますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '名称やロゴを掲載していない場合でも、介護・医療・音楽等の指定役務として「パタカラ」を用いたサービスやプログラムを実施・提供する行為には、商標権の効力が及びます。施設サービスとして実施する場合は、事前に公式施設ライセンスをご取得いただくか、当社へご相談ください。',
            },
          },
          {
            '@type': 'Question',
            name: '利用者に「パタカラ」を発声させる場合もライセンスが必要ですか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '介護・医療施設等が提供する口腔機能向上サービスやプログラムとして、利用者に「パタカラ」を発声させる場合は、公式施設ライセンスの対象となります。家庭内で個人的に発声する場合など、事業または施設サービスとして提供されない私的な利用を制限するものではありません。',
            },
          },
          {
            '@type': 'Question',
            name: 'カラオケを使って実施する場合も対象になりますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '介護・医療施設等が、カラオケ機器やカラオケ音源を使用して「パタカラ」を実施・提供する場合も、公式施設ライセンスの対象となります。',
            },
          },
          {
            '@type': 'Question',
            name: 'すでにWEBサイトや資料で使用しています。どうすればよいですか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '現在の利用方法が分かるWEBサイトのURL、パンフレット、施設内資料等を添えてお問い合わせください。内容を確認し、公式施設ライセンスの適用または必要な対応をご案内します。',
            },
          },
          {
            '@type': 'Question',
            name: '公式ロゴを使用できますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '公式ロゴの使用には、公式施設ライセンスまたは当社の個別許諾が必要です。ロゴの色、形、文字の配置等を変更せず、当社が提供する正式なロゴデータをご使用ください。',
            },
          },
          {
            '@type': 'Question',
            name: '自治体や官公庁にも月額費用がかかりますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '地域の健康増進、介護予防、福祉活動など、非営利の公的事業における利用は、原則として有料ライセンスの対象外です。ご利用前に利用目的、実施内容および使用媒体をご連絡いただき、内容確認後に無料の使用許可証を発行します。',
            },
          },
          {
            '@type': 'Question',
            name: '許諾団体から受け取った資料を別の施設で使用できますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '許諾は、当社が確認した自治体・団体、利用目的、実施内容、使用媒体および使用範囲に限り有効です。許諾団体から資料を受け取った場合でも、別の施設や団体がそのまま「パタカラ®」を利用できるものではありません。利用を希望する施設・団体から、当社へ直接ご相談ください。',
            },
          },
        ],
      },
    ];

    const existingScripts = document.querySelectorAll('script[data-schema="trademark"]');
    existingScripts.forEach((s) => s.remove());

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'trademark');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="trademark"]').forEach((s) => s.remove());
    };
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const openLightbox = (src: string, alt: string) => {
    setLightboxSrc(src);
    setLightboxAlt(alt);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/1827e98d95d9f7558266c3fb82d75725/927c3b547f45ed1f38c15098c60865bf.png"
                alt="パタカラ®ロゴ"
                className="h-12 w-auto object-contain"
              />
            </Link>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">
              公式施設ライセンス
            </Link>
            <span className="text-sm font-medium text-orange-500 whitespace-nowrap">
              商標利用ガイドライン
            </span>
            <Link to="/contact" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors whitespace-nowrap">
              お問合せ
            </Link>
            <Link
              to="/license-application"
              className="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer"
            >
              申込む
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-gray-700 cursor-pointer"
            aria-label={mobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="fixed top-[72px] left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg md:hidden">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-line text-lg w-5 h-5 flex items-center justify-center text-gray-400"></i>
              公式施設ライセンス
            </Link>
            <Link
              to="/trademark-guidelines"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-orange-500 bg-orange-50 cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-shield-check-line text-lg w-5 h-5 flex items-center justify-center text-orange-500"></i>
              商標利用ガイドライン
            </Link>
            <Link
              to="/contact"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-mail-line text-lg w-5 h-5 flex items-center justify-center text-gray-400"></i>
              お問合せ
            </Link>
            <Link
              to="/license-application"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 transition-all cursor-pointer mt-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-edit-line text-lg w-5 h-5 flex items-center justify-center"></i>
              申込む
            </Link>
          </div>
        </div>
      )}

      {/* Hero Header */}
      <header className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        {/* Background Image */}
        <img
          src="https://readdy.ai/api/search-image?query=Bright%20fresh%20green%20gradient%20background%20with%20vibrant%20lime%20and%20spring%20green%20tones%20radiating%20from%20center%2C%20soft%20organic%20flowing%20abstract%20shapes%20with%20gentle%20wave%20patterns%2C%20clean%20airy%20atmosphere%20with%20diffused%20natural%20light%20rays%2C%20fresh%20energetic%20moss%20green%20and%20chartreuse%20hues%20blending%20smoothly%2C%20professional%20yet%20lively%20editorial%20illustration%20suitable%20for%20trademark%20guidelines%20header%2C%20high%20quality%20minimal%20design%20with%20uplifting%20natural%20feeling&width=1600&height=600&seq=trademark-hero-bg-03&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Green Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-green-500/45 via-emerald-400/30 to-slate-800/55" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur-sm rounded-full border border-white/20 mb-6" style={{ textShadow: '0 2px 6px rgba(0,0,0,0.5)' }}>
            <i className="ri-shield-check-line text-green-300 text-sm"></i>
            <span className="text-sm font-medium text-white/90">登録商標 第5657576号</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-serif font-bold text-white mb-5 leading-tight" style={{ textShadow: '0 3px 12px rgba(0,0,0,0.55)' }}>
            「パタカラ®」商標利用ガイドライン
          </h1>
          <p className="text-base md:text-lg text-white/85 leading-relaxed max-w-2xl mx-auto" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
            登録商標「パタカラ®」を安心して正しくご活用いただくための公式ガイドラインです
          </p>
        </div>
      </header>

      {/* Table of Contents */}
      <div className="max-w-4xl mx-auto px-6 -mt-8 mb-8 relative z-10">
        <TableOfContents />
      </div>

      {/* Main Content */}
      <main>
        <IntroSection onImageClick={openLightbox} />
        <RegistrationSection onImageClick={openLightbox} />
        <ScopeSection onImageClick={openLightbox} />
        <UsageExamplesSection onImageClick={openLightbox} />
        <LicenseSection onImageClick={openLightbox} />
        <CurrentUsersSection />
        <GovernmentSection />
        <CasesSection />
        <FAQSection openFaq={openFaq} toggleFaq={toggleFaq} />
        <ClosingSection />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/1827e98d95d9f7558266c3fb82d75725/927c3b547f45ed1f38c15098c60865bf.png"
                  alt="パタカラ®ロゴ"
                  className="h-10 w-auto object-contain mb-4 bg-white rounded-lg p-1.5"
                />
              </Link>
              <p className="text-sm text-gray-400 leading-relaxed">
                介護施設・医療施設向けの公式ライセンスサービス
              </p>
            </div>

            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">サービス</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">概要</Link></li>
                <li><Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">できること</Link></li>
                <li><Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">詳細</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">サポート</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">申込の流れ</Link></li>
                <li><Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">よくある質問</Link></li>
                <li><Link to="/terms" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">利用規約</Link></li>
                <li><Link to="/contact" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">お問い合わせ</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">会社情報</h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-2">
                株式会社パックスシステムズ
              </p>
              <p className="text-xs text-gray-500 leading-relaxed mb-3">
                担当：関<br />
                〒231-0023<br />
                横浜市中区山下町1番地<br />
                シルクセンターM1F<br />
                TEL: 045-681-3165<br />
                FAX: 045-681-3173<br />
                メール：info@patakara.org
              </p>
              <p className="text-xs text-gray-500 leading-relaxed">
                登録商標「パタカラ®」<br />
                登録第5657576号
              </p>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-500 space-y-1">
                <p>© 2026 Pax Systems Co., Ltd. All rights reserved.</p>
                <p className="text-xs text-gray-500">「パタカラ®」は株式会社パックスシステムズの登録商標です（登録第5657576号）。</p>
                <p className="text-xs text-gray-500">本サイトは登録商標の指定役務の範囲内におけるライセンス提供について案内するものです。</p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-full max-h-full">
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white text-2xl hover:text-gray-300 transition-colors cursor-pointer"
              aria-label="閉じる"
            >
              <i className="ri-close-line"></i>
            </button>
            <img
              src={lightboxSrc}
              alt={lightboxAlt}
              className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}